import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { ArrowClockwise, X, ArrowsCounterClockwise, CheckCircle, Trophy, Printer, FilePdf, FileXls } from '@phosphor-icons/react';
import api from '../../utils/api';

/**
 * Finals Qualifiers panel — shown when the producer opens a class flagged
 * `is_finals`. Lists riders who advanced from each source Go, grouped per Go,
 * with the time they qualified at. Producer can drop a qualifier (rider
 * scratched, etc.) and click Refresh to fill the slot with the next-ranked
 * rider from the same Go.
 *
 * Confirm turns the current list into REAL $0 entries on the Finals class
 * so they appear on the Sunday draw.
 */
export default function FinalsQualifiersPanel({ showId, finalsClass, canEdit, onConfirmed }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.get(`/shows/${showId}/classes/${finalsClass.id}/qualifiers`);
      setData(resp);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load qualifiers');
    } finally {
      setLoading(false);
    }
  }, [showId, finalsClass.id]);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.post(`/shows/${showId}/classes/${finalsClass.id}/qualifiers/refresh`);
      setData(resp);
      toast.success('Qualifiers refreshed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refresh failed');
    } finally {
      setLoading(false);
    }
  };

  const drop = async (q) => {
    if (!window.confirm(`Drop ${q.rider_name} (${q.horse_name}) from Finals? The next-ranked rider from the same Go will take this slot.`)) return;
    try {
      await api.patch(`/shows/${showId}/classes/${finalsClass.id}/qualifiers/${q.id}/drop`);
      // Auto-refresh so the next rider fills the slot.
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Drop failed');
    }
  };

  const restore = async (q) => {
    try {
      await api.patch(`/shows/${showId}/classes/${finalsClass.id}/qualifiers/${q.id}/restore`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore failed');
    }
  };

  const confirmAll = async () => {
    if (!window.confirm('Lock these qualifiers in as Finals entries? Riders will appear on the Sunday draw list.')) return;
    setConfirming(true);
    try {
      const { data: resp } = await api.post(`/shows/${showId}/classes/${finalsClass.id}/qualifiers/confirm`);
      toast.success(`Confirmed: ${resp.created} entries created (${resp.skipped_duplicates} already entered)`);
      if (onConfirmed) onConfirmed();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Confirm failed');
    } finally {
      setConfirming(false);
    }
  };

  // v247 — Export / Print. Endpoints are public read (parity with GET
  // /qualifiers); we hit them directly with a URL rather than through
  // axios so the browser can stream + display / download natively.
  const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
  const pdfUrl = `${BACKEND}/api/shows/${showId}/classes/${finalsClass.id}/qualifiers/export.pdf`;
  const xlsxUrl = `${BACKEND}/api/shows/${showId}/classes/${finalsClass.id}/qualifiers/export.xlsx`;
  const openPrint = () => {
    // inline=1 -> Content-Disposition: inline so the browser PDF viewer
    // opens (with its native Print button) instead of downloading.
    window.open(`${pdfUrl}?inline=1`, '_blank', 'noopener');
  };
  const downloadPdf = () => { window.open(pdfUrl, '_blank', 'noopener'); };
  const downloadXlsx = () => { window.open(xlsxUrl, '_blank', 'noopener'); };

  if (!data && loading) return <div className="p-6 text-center text-[#9E9E9E] text-sm">Loading qualifiers…</div>;
  if (!data) return null;

  const topN = data.top_n;
  const perDivision = !!data.per_division;
  const totalQualified = data.groups.reduce((sum, g) => sum + g.qualifiers.length, 0);

  // v245 — When per_division is on, group each Go's qualifiers by division
  // for display so producers can immediately see "3 from 1D, 3 from 2D...".
  const DIVISION_ORDER = ['1D', '2D', '3D', '4D'];
  const groupByDivision = (rows) => {
    const buckets = {};
    for (const r of rows) {
      const key = r.division || 'Other';
      if (!buckets[key]) buckets[key] = [];
      buckets[key].push(r);
    }
    // Preserve canonical order (1D→4D) then any custom labels alpha.
    const sortedKeys = Object.keys(buckets).sort((a, b) => {
      const ai = DIVISION_ORDER.indexOf(a);
      const bi = DIVISION_ORDER.indexOf(b);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.localeCompare(b);
    });
    return sortedKeys.map(k => ({ division: k, rows: buckets[k] }));
  };

  return (
    <div className="p-5" data-testid="finals-qualifiers-panel">
      <div className="flex items-start justify-between mb-4 gap-3">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <Trophy size={18} weight="fill" className="text-[#FACC15]" />
            Finals Qualifiers
          </h3>
          <p className="text-xs text-[#9E9E9E] mt-0.5">
            {perDivision
              ? `Top ${topN} per Division per Go`
              : `Top ${topN} per Go`} &middot; {totalQualified} qualified so far &middot; duplicates auto-skipped
          </p>
        </div>
        {canEdit && (
          <div className="flex gap-2 flex-shrink-0 flex-wrap justify-end">
            <button
              type="button"
              onClick={refresh}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#FACC15]/10 border border-[#FACC15]/40 hover:bg-[#FACC15]/20 text-[#FACC15] rounded-md text-xs font-bold transition-colors disabled:opacity-50"
              data-testid="qualifiers-refresh"
              title="Recompute after a Go finishes scoring"
            >
              <ArrowClockwise size={14} weight="bold" /> Refresh
            </button>
            <button
              type="button"
              onClick={openPrint}
              disabled={totalQualified === 0}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#0096FF]/10 border border-[#0096FF]/40 hover:bg-[#0096FF]/20 text-[#0096FF] rounded-md text-xs font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="qualifiers-print"
              title="Open a printable PDF in a new tab"
            >
              <Printer size={14} weight="bold" /> Print
            </button>
            <button
              type="button"
              onClick={downloadPdf}
              disabled={totalQualified === 0}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#161616] border border-[#333] hover:border-[#0096FF] text-white rounded-md text-xs font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="qualifiers-export-pdf"
              title="Download the qualifier list as PDF"
            >
              <FilePdf size={14} weight="bold" /> PDF
            </button>
            <button
              type="button"
              onClick={downloadXlsx}
              disabled={totalQualified === 0}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#161616] border border-[#333] hover:border-[#34C759] text-white rounded-md text-xs font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="qualifiers-export-xlsx"
              title="Download the qualifier list as an Excel workbook"
            >
              <FileXls size={14} weight="bold" /> Excel
            </button>
            <button
              type="button"
              onClick={confirmAll}
              disabled={confirming || totalQualified === 0}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#34C759]/10 border border-[#34C759]/40 hover:bg-[#34C759]/20 text-[#34C759] rounded-md text-xs font-bold transition-colors disabled:opacity-50"
              data-testid="qualifiers-confirm"
              title="Create real $0 entries for these qualifiers"
            >
              <CheckCircle size={14} weight="bold" /> {confirming ? 'Locking…' : 'Lock in Finals Draw'}
            </button>
          </div>
        )}
      </div>

      {data.groups.length === 0 && (
        <div className="px-4 py-6 bg-[#0A0A0A] border border-[#262626] rounded-lg text-center text-sm text-[#9E9E9E]" data-testid="qualifiers-empty">
          No source Gos configured. Mark sibling classes in the same Multi-Go Group, then click Refresh.
        </div>
      )}

      <div className="space-y-4">
        {data.groups.map((g) => (
          <div key={g.source_class_id} className="bg-[#0A0A0A] border border-[#262626] rounded-lg overflow-hidden" data-testid={`qualifier-group-${g.source_class_id}`}>
            <div className="px-4 py-2 bg-[#161616] border-b border-[#262626] flex items-center justify-between">
              <p className="text-sm font-bold text-white">{g.source_class_name}</p>
              <p className="text-xs text-[#9E9E9E]">
                {g.qualifiers.length}
                {perDivision ? ' qualified' : ` of top ${topN}`}
              </p>
            </div>
            {g.qualifiers.length === 0 ? (
              <div className="px-4 py-3 text-xs text-[#555] text-center">No completed runs yet — refresh after scoring.</div>
            ) : perDivision ? (
              // v245 — per-division view: one sub-header + list per D bucket.
              <div className="divide-y divide-[#262626]">
                {groupByDivision(g.qualifiers).map(({ division, rows }) => (
                  <div key={division} data-testid={`qualifier-division-${g.source_class_id}-${division}`}>
                    <div className="px-4 py-1.5 bg-[#0F0F0F] flex items-center justify-between">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-[#0096FF]">{division}</span>
                      <span className="text-[10px] text-[#555]">{rows.length} of top {topN}</span>
                    </div>
                    {rows.map((q, idx) => (
                      <div key={q.id} className="px-4 py-2 flex items-center gap-3 hover:bg-[#161616] transition-colors" data-testid={`qualifier-row-${q.id}`}>
                        <span className="w-7 text-center text-sm font-black text-[#FACC15]">{idx + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-white truncate">{q.rider_name}</p>
                          <p className="text-xs text-[#9E9E9E] truncate">{q.horse_name}</p>
                        </div>
                        <span className="text-sm font-bold text-[#0096FF] tabular-nums">{Number(q.qualifying_time).toFixed(3)}s</span>
                        {canEdit && (
                          <button
                            type="button"
                            onClick={() => drop(q)}
                            className="p-1.5 text-[#FF3B30]/70 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded transition-colors"
                            data-testid={`qualifier-drop-${q.id}`}
                            title="Drop qualifier — next-ranked rider takes the slot on refresh"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <div className="divide-y divide-[#262626]">
                {g.qualifiers.map((q, idx) => (
                  <div key={q.id} className="px-4 py-2 flex items-center gap-3 hover:bg-[#161616] transition-colors" data-testid={`qualifier-row-${q.id}`}>
                    <span className="w-7 text-center text-sm font-black text-[#FACC15]">{idx + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white truncate">{q.rider_name}</p>
                      <p className="text-xs text-[#9E9E9E] truncate">{q.horse_name}{q.division ? ` • ${q.division}` : ''}</p>
                    </div>
                    <span className="text-sm font-bold text-[#0096FF] tabular-nums">{Number(q.qualifying_time).toFixed(3)}s</span>
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => drop(q)}
                        className="p-1.5 text-[#FF3B30]/70 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded transition-colors"
                        data-testid={`qualifier-drop-${q.id}`}
                        title="Drop qualifier — next-ranked rider takes the slot on refresh"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {data.dropped && data.dropped.length > 0 && (
        <div className="mt-4 bg-[#0A0A0A] border border-[#FF3B30]/30 rounded-lg overflow-hidden">
          <div className="px-4 py-2 bg-[#FF3B30]/10 border-b border-[#FF3B30]/30">
            <p className="text-sm font-bold text-[#FF3B30]">Dropped ({data.dropped.length})</p>
            <p className="text-[10px] text-[#9E9E9E]">These riders won't be auto-picked again. Restore to undo.</p>
          </div>
          <div className="divide-y divide-[#262626]">
            {data.dropped.map(q => (
              <div key={q.id} className="px-4 py-2 flex items-center gap-3" data-testid={`qualifier-dropped-${q.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#9E9E9E] line-through truncate">{q.rider_name}</p>
                  <p className="text-xs text-[#555] truncate">{q.horse_name}</p>
                </div>
                <span className="text-xs text-[#555] tabular-nums">{Number(q.qualifying_time).toFixed(3)}s</span>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => restore(q)}
                    className="p-1.5 text-[#FACC15]/70 hover:text-[#FACC15] hover:bg-[#FACC15]/10 rounded transition-colors"
                    data-testid={`qualifier-restore-${q.id}`}
                    title="Restore qualifier"
                  >
                    <ArrowsCounterClockwise size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
